/* Phase 62 — Sent Emails history page.
 *
 * Lists every email send across all invoices in the tenant. Hits
 * `GET /api/invoices/sent` with date/customer/text filters + paginates.
 * Per-row actions: View Invoice (opens InvoiceDetail) and Re-send (opens
 * the InvoiceDetail send dialog pre-filled — that wiring lives on the
 * detail page itself; this list just hands off via querystring).
 *
 * Backfilled rows (pre-Phase-62) are flagged `is_historical=true` and
 * show "(details unavailable)" for subject/body. A banner above the list
 * warns the user about it explicitly.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, ChevronLeft, ChevronRight, ExternalLink, RotateCw } from "lucide-react";

export default function SentEmails() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState(searchParams.get("from") || "");
  const [to, setTo] = useState(searchParams.get("to") || "");
  const [q, setQ] = useState(searchParams.get("q") || "");
  const limit = 50;
  const hasHistorical = rows.some((r) => r.is_historical);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (from) params.from = from;
      if (to) params.to = to;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/invoices/sent", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, from, to]);

  const applyFilters = () => { setPage(1); load(); };

  return (
    <div className="space-y-6" data-testid="sent-emails-page">
      <div className="flex items-center gap-3">
        <Mail className="h-5 w-5 text-accent" />
        <h1 className="text-2xl font-black tracking-tight">Sent emails</h1>
        <Badge variant="outline" className="ml-auto" data-testid="sent-emails-total">
          {total} total
        </Badge>
      </div>

      {hasHistorical && (
        <Card className="p-3 bg-amber-50 border-amber-200 text-amber-900 text-sm" data-testid="sent-emails-historical-banner">
          Send details before <strong>Phase 62</strong> were not captured.
          Older entries show only date + recipients; subject and body appear as &quot;—&quot;.
        </Card>
      )}

      <Card className="p-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-xs font-semibold text-muted-foreground">From</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="sent-emails-from-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">To</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="sent-emails-to-input" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-muted-foreground">Search invoice # or customer</label>
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="I-000123 or Acme Ltd" data-testid="sent-emails-search-input" />
            <Button onClick={applyFilters} variant="default" data-testid="sent-emails-search-button">Search</Button>
          </div>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2">Sent at</th>
              <th className="text-left px-3 py-2">Invoice #</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Recipients</th>
              <th className="text-left px-3 py-2">By</th>
              <th className="text-left px-3 py-2">Accounts CC</th>
              <th className="text-left px-3 py-2">Custom</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No sent emails match your filters.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`sent-row-${r.id}`}>
                <td className="px-3 py-2 mono text-xs">{(r.sent_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2 mono font-semibold">{r.invoice_number || "—"}</td>
                <td className="px-3 py-2">{r.customer_name || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(r.recipients || []).map((e, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px]">{e}</Badge>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{r.sent_by_user_email || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {r.included_accounts_email === true ? "Yes" : r.included_accounts_email === false ? "No" : "—"}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.is_historical
                    ? <Badge variant="outline" className="text-[10px]">historical</Badge>
                    : (r.custom_body_used ? "Yes" : "—")}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <Link to={`/invoices/${r.invoice_id}`} data-testid={`sent-row-view-${r.id}`}>
                      <Button size="sm" variant="outline" className="h-7"><ExternalLink className="h-3 w-3 mr-1" /> View</Button>
                    </Link>
                    <Link to={`/invoices/${r.invoice_id}?resend=${r.id}`} data-testid={`sent-row-resend-${r.id}`}>
                      <Button size="sm" variant="outline" className="h-7"><RotateCw className="h-3 w-3 mr-1" /> Re-send</Button>
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div>Page {page} · showing {rows.length} of {total}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="sent-emails-prev">
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button size="sm" variant="outline" disabled={!hasMore} onClick={() => setPage((p) => p + 1)} data-testid="sent-emails-next">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
